import type { ReactNode } from 'react';
import { Link, useRecordContext, type RaRecord } from 'react-admin';
import { Box, Button, Stack, Typography } from '@mui/material';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { shortDateTime } from '../../shared/dateTime';
import { fmtCalendarDate } from '../../shared/Panel';
import { plural } from '../../shared/plural';
import { ALERT_BUTTON_SX, OrderAlertCard } from '../userOffers/OrderAlertCard';
import {
  REPLAN_ALERT_ORIGIN,
  REPLAN_IMPORTANT_TITLE,
  replanAlertText,
  replanReasonText,
} from '../userOffers/ReplanAlerts';
import { TMS_ALERT_ORIGIN, tmsAlertLines, tmsAlertTitle } from '../userOffers/TmsChangeAlerts';
import { isImportantAlert, type TmsAlert } from '../userOffers/orderActionsApi';
import { AvailabilityCell, DriverCell } from './cells';
import { SOON_DAYS, daysUntil, sortByTerm, type VehicleEligibilityRow } from './eligibilityTerms';
import { showOrderSuggestions, useCarCard } from './CarCardContext';
import { currentAndNext } from './ganttRules';
import { planPhase } from './planRows';
import { durationText } from './planSegments';
import { routeText } from './timeline';
import type { PlanItem } from './vehiclePlanApi';

/**
 * Pasek podsumowania i baner alertu na karcie pojazdu (07.10.2026, prototyp
 * p. Mariusza, zmiany 1 i 5): odpowiedź na „czy ten pojazd da radę” bez
 * przewijania — stan, kierowca, bieżące i następne zlecenie, alerty zleceń
 * i najbliższy termin z kartoteki — a nad harmonogramem alert z przejściem
 * do rekomendacji.
 *
 * Plan czytamy w domyślnym oknie API (`CarCardContext`), więc „bieżące”
 * i „następne” liczą się od teraz, niezależnie od przewinięcia osi. Alert to
 * ten sam wpis i ta sama reguła co baner na karcie zlecenia — potwierdza się
 * go tam („Przyjmuję do wiadomości”), tu tylko informacja i przejścia.
 */

type CarRecord = RaRecord & Record<string, unknown>;

const WARNING_TEXT = '#c75a00';

/** „za 14 h 48 min”, „za 2 dni 3 h”. */
function inText(ms: number): string {
  if (ms < 86_400_000) return `za ${durationText(ms)}`;
  const hours = Math.floor(ms / 3_600_000);
  const d = Math.floor(hours / 24);
  const h = hours % 24;
  return `za ${plural(d, 'dzień', 'dni', 'dni')}${h > 0 ? ` ${h} h` : ''}`;
}

const Cell = ({ label, children, testId }: { label: string; children: ReactNode; testId: string }) => (
  <Box
    data-testid={testId}
    sx={{ px: 1.75, py: 1.25, minWidth: 0, borderRight: '1px solid', borderColor: 'divider', '&:last-child': { borderRight: 0 } }}
  >
    <Typography
      variant="caption"
      sx={{ display: 'block', mb: 0.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, lineHeight: 1.2 }}
    >
      {label}
    </Typography>
    <Box sx={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.35 }}>{children}</Box>
  </Box>
);

const Sub = ({ children }: { children: ReactNode }) => (
  <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 400 }}>
    {children}
  </Typography>
);

const OrderLine = ({ item }: { item: PlanItem | null }) =>
  item ? (
    <>
      <Link to={`/user-offers/${item.orderId}/show`}>{item.number ?? `#${item.orderId}`}</Link>
      <Sub>{routeText(item)}</Sub>
    </>
  ) : (
    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
      brak
    </Box>
  );

const ALERT_TITLE: Record<TmsAlert['kind'], string> = {
  replan: 'Rekomendowane przeplanowanie',
  change: 'Zmiana w TMS po obsadzeniu',
  cancellation: 'Anulacja w TMS — do potwierdzenia',
  vehicleChange: 'Zmiana pojazdu w TMS po obsadzeniu',
};

/** Obsadzenia (niezakończone) z alertami czekającymi na potwierdzenie. */
export function alertedItems(items: readonly PlanItem[]): PlanItem[] {
  return items.filter((i) => i.kind === 'assignment' && planPhase(i) !== 'done' && (i.alerts ?? []).length > 0);
}

export const CarSummary = ({ now = new Date() }: { now?: Date }) => {
  const r = useRecordContext<CarRecord>();
  const { overview } = useCarCard();
  if (!r) return null;
  const items = overview.plan?.items ?? [];
  const { current, next } = currentAndNext(items, now);
  const alerted = alertedItems(items);
  const alertCount = alerted.reduce((sum, i) => sum + (i.alerts ?? []).length, 0);
  const firstAlert = alerted[0]?.alerts?.[0];
  const eligibilities = Array.isArray(r.eligibilities) ? (r.eligibilities as VehicleEligibilityRow[]) : [];
  const nearest = sortByTerm(eligibilities).find((e) => daysUntil(e.validUpTo, now) !== null) ?? null;
  const nearestDays = nearest ? daysUntil(nearest.validUpTo, now) : null;
  const pending = overview.loading ? '…' : null;
  const planUnknown = overview.error ? 'nie udało się wczytać planu' : null;

  return (
    <Box
      data-testid="car-summary"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Cell label="Stan" testId="summary-status">
        <AvailabilityCell record={r} />
      </Cell>
      <Cell label="Kierowca" testId="summary-driver">
        <DriverCell record={r} />
        {typeof r.ftlCaretaker === 'string' && r.ftlCaretaker ? <Sub>opiekun FTL: {r.ftlCaretaker}</Sub> : null}
      </Cell>
      <Cell label="Bieżące zlecenie" testId="summary-current">
        {pending ?? planUnknown ?? <OrderLine item={current} />}
      </Cell>
      <Cell label="Następne zlecenie" testId="summary-next">
        {pending ?? planUnknown ?? (
          <>
            <OrderLine item={next} />
            {next?.occupancyFrom ? (
              <Sub>
                start {shortDateTime(next.occupancyFrom)} ·{' '}
                {inText(new Date(next.occupancyFrom).getTime() - now.getTime())}
              </Sub>
            ) : null}
          </>
        )}
      </Cell>
      <Cell label="Alerty zleceń" testId="summary-alerts">
        {pending ?? planUnknown ?? (
          <Box component="span" sx={alertCount > 0 ? { color: WARNING_TEXT } : undefined}>
            {alertCount > 0 ? (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <WarningAmberRoundedIcon sx={{ fontSize: 18 }} /> {alertCount}
              </Box>
            ) : (
              '0'
            )}
            {firstAlert ? <Sub>{ALERT_TITLE[firstAlert.kind]}</Sub> : null}
          </Box>
        )}
      </Cell>
      <Cell label="Najbliższy termin" testId="summary-term">
        {nearest && nearestDays !== null ? (
          <Box component="span" sx={nearestDays <= SOON_DAYS ? { color: WARNING_TEXT } : undefined}>
            {fmtCalendarDate(nearest.validUpTo)}
            <Sub>
              {nearest.name.toLowerCase()} ·{' '}
              {nearestDays < 0 ? 'wygasło' : nearestDays === 0 ? 'dziś' : `za ${plural(nearestDays, 'dzień', 'dni', 'dni')}`}
            </Sub>
          </Box>
        ) : (
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            brak terminów w kartotece
          </Box>
        )}
      </Cell>
    </Box>
  );
};

/** Notka w stopce banera — gdzie się potwierdza i że nic się nie zmieniło. */
export const CAR_ALERT_NOTE =
  'System niczego nie zmienił. Alert przyjmujesz do wiadomości na karcie zlecenia; o przeplanowaniu decydujesz Ty.';

/**
 * Baner alertu nad harmonogramem (zmiana 1) — dla każdego obsadzenia
 * z alertem: przeplanowanie osobno od zmian w TMS, jak na karcie zlecenia.
 * Ważność jak na karcie zlecenia (12.10.2026): zmiany w TMS czerwone,
 * przeplanowanie czerwone tylko z pewnym powodem, inaczej stonowane.
 */
export const CarAlertBanners = () => {
  const { overview } = useCarCard();
  const items = alertedItems(overview.plan?.items ?? []);
  if (items.length === 0) return null;
  return (
    <Stack spacing={1.5} data-testid="car-alert-banners">
      {items.flatMap((item) => {
        const alerts = item.alerts ?? [];
        const number = item.number ?? `#${item.orderId}`;
        const groups: Array<{ key: string; alerts: TmsAlert[] }> = [
          { key: 'replan', alerts: alerts.filter((a) => a.kind === 'replan') },
          { key: 'tms', alerts: alerts.filter((a) => a.kind !== 'replan') },
        ];
        return groups
          .filter((g) => g.alerts.length > 0)
          .map((g) => {
            const replan = g.key === 'replan';
            const important = g.alerts.some((a) => isImportantAlert(a));
            return (
              <OrderAlertCard
                key={`${item.orderId}-${g.key}`}
                tone={important ? 'error' : 'muted'}
                title={`${replan ? (important ? REPLAN_IMPORTANT_TITLE : ALERT_TITLE.replan) : tmsAlertTitle(g.alerts)} · ${number}`}
                detectedAt={g.alerts.map((a) => a.at)}
                origin={replan ? REPLAN_ALERT_ORIGIN : TMS_ALERT_ORIGIN}
                lines={
                  replan
                    ? g.alerts.map((a) => ({
                        id: a.id,
                        text: replanAlertText(a.text),
                        detail: replanReasonText(a.reason),
                      }))
                    : tmsAlertLines(g.alerts)
                }
                note={CAR_ALERT_NOTE}
                actions={
                  <>
                    <Button
                      variant="outlined"
                      component={Link}
                      to={`/user-offers/${item.orderId}/show`}
                      endIcon={<OpenInNewRoundedIcon />}
                      sx={ALERT_BUTTON_SX}
                    >
                      Otwórz zlecenie
                    </Button>
                    {replan ? (
                      <Button
                        variant="contained"
                        disableElevation
                        endIcon={<ArrowDownwardRoundedIcon />}
                        sx={ALERT_BUTTON_SX}
                        onClick={() => showOrderSuggestions()}
                      >
                        Pokaż rekomendowane zlecenia
                      </Button>
                    ) : null}
                  </>
                }
              />
            );
          });
      })}
    </Stack>
  );
};
